import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Calendar, Users, Clock, ArrowRight, Link, Copy, TrendingUp, QrCode, ExternalLink, Check, X as XIcon, Plus, Percent, UserCheck, BarChart3, Scissors, Star, Bell } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon apr\u00e8s-midi";
  return "Bonsoir";
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayAppts, setTodayAppts] = useState([]);
  const [shop, setShop] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [insights, setInsights] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [walkinForm, setWalkinForm] = useState({ service_id: "", client_name: "", client_phone: "" });
  const [walkinLoading, setWalkinLoading] = useState(false);

  const loadData = useCallback(() => {
    Promise.all([
      API.get("/appointments/stats"),
      API.get("/appointments/today"),
      API.get("/barbershop"),
      API.get("/appointments/revenue"),
      API.get("/appointments/insights"),
      API.get("/services"),
    ]).then(([s, a, sh, r, ins, svc]) => {
      setStats(s.data); setTodayAppts(a.data); setShop(sh.data);
      setRevenue(r.data); setInsights(ins.data); setServices(svc.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const bookingUrl = shop?.slug ? `${window.location.origin}/book/${shop.slug}` : null;
  const qrUrl = bookingUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(bookingUrl)}` : null;
  const copyLink = () => { if (bookingUrl) { navigator.clipboard.writeText(bookingUrl); toast.success("Lien copi\u00e9 !"); } };

  const nextAppt = todayAppts.find(a => a.status === "confirmed");

  const updateStatus = async (id, status) => {
    try {
      await API.put(`/appointments/${id}/status?status=${status}`);
      toast.success(status === "completed" ? "RDV termin\u00e9" : "RDV annul\u00e9");
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const handleWalkin = async () => {
    if (!walkinForm.service_id || !walkinForm.client_name.trim()) { toast.error("Remplissez les champs"); return; }
    setWalkinLoading(true);
    const now = new Date();
    const h = String(now.getHours()).padStart(2, "0");
    const m = now.getMinutes() < 30 ? "00" : "30";
    try {
      await API.post("/appointments", {
        service_id: walkinForm.service_id, date: format(now, "yyyy-MM-dd"),
        start_time: `${h}:${m}`, client_name: walkinForm.client_name, client_phone: walkinForm.client_phone || "Non renseign\u00e9",
      });
      toast.success("Walk-in ajout\u00e9 !");
      setWalkinOpen(false);
      setWalkinForm({ service_id: "", client_name: "", client_phone: "" });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWalkinLoading(false); }
  };

  const statusColors = { confirmed: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };
  const statusLabels = { confirmed: "Confirm\u00e9", completed: "Termin\u00e9", cancelled: "Annul\u00e9" };

  if (loading) return (
    <div className="space-y-4" data-testid="dashboard-loading">
      <Skeleton className="h-10 w-64" /><Skeleton className="h-32 w-full" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      <Skeleton className="h-48 w-full" /><Skeleton className="h-64 w-full" />
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{getGreeting()}, {user?.name?.split(" ")[0]} !</h1>
          <p className="text-muted-foreground text-xs mt-0.5 capitalize">{format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}</p>
        </div>
        <Button size="sm" onClick={() => setWalkinOpen(true)} className="rounded-full gap-1.5 text-xs bg-[#2563EB] hover:bg-blue-700 self-start sm:self-auto" data-testid="walkin-btn">
          <Plus size={14} /> Walk-in
        </Button>
      </div>

      {/* Next Appointment */}
      {nextAppt && (
        <div className="bg-[#2563EB] text-white rounded-2xl p-5 sm:p-6 relative overflow-hidden" data-testid="next-appointment-card">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-12 translate-x-12" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-10 -translate-x-10" />
          <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-3">Prochain client</p>
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center font-heading font-bold text-base sm:text-lg">
                {nextAppt.client_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-base sm:text-lg">{nextAppt.client_name}</p>
                <p className="text-white/60 text-xs sm:text-sm">{nextAppt.service_name} &middot; {nextAppt.duration} min &middot; {nextAppt.price}&euro;</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-2xl sm:text-3xl font-bold">{nextAppt.start_time}</p>
            </div>
          </div>
        </div>
      )}

      {/* Booking Link + QR */}
      {bookingUrl && (
        <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0"><Link size={16} className="text-[#2563EB]" /></div>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Lien de r&eacute;servation</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">{bookingUrl}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <Button size="sm" variant="outline" onClick={copyLink} className="rounded-full gap-1.5 text-xs h-8" data-testid="copy-link-btn"><Copy size={12} /> Copier</Button>
              <Button size="sm" variant="outline" onClick={() => setShowQr(!showQr)} className="rounded-full gap-1.5 text-xs h-8" data-testid="qr-toggle-btn"><QrCode size={12} /> QR</Button>
              <Button size="sm" onClick={() => window.open(bookingUrl, "_blank")} className="rounded-full gap-1.5 text-xs h-8 bg-[#2563EB] hover:bg-blue-700" data-testid="open-booking-btn"><ExternalLink size={12} /></Button>
            </div>
          </div>
          {showQr && qrUrl && (
            <div className="mt-4 pt-4 border-t border-border flex flex-col items-center gap-3 animate-fade-up" data-testid="qr-section">
              <img src={qrUrl} alt="QR Code" className="w-36 h-36 rounded-lg border border-border" />
              <p className="text-[11px] text-muted-foreground">Imprimez-le et affichez-le dans votre salon</p>
              <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => { const a = document.createElement("a"); a.href = qrUrl; a.download = "cutit-qr.png"; a.click(); }}>T&eacute;l&eacute;charger</Button>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Aujourd'hui", value: stats?.today ?? "-", icon: Calendar, color: "text-[#2563EB]", bg: "bg-blue-50" },
          { label: "Cette semaine", value: stats?.week ?? "-", icon: TrendingUp, color: "text-green-600", bg: "bg-green-50" },
          { label: "Ce mois", value: stats?.month ?? "-", icon: Clock, color: "text-orange-600", bg: "bg-orange-50" },
          { label: "Clients", value: stats?.total_clients ?? "-", icon: Users, color: "text-purple-600", bg: "bg-purple-50" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-border p-4 hover:shadow-soft transition-shadow" data-testid={`stat-${i}`}>
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2`}><s.icon size={15} className={s.color} /></div>
            <p className="font-mono text-2xl sm:text-3xl font-bold">{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Revenue + Insights Row */}
      <div className="grid lg:grid-cols-5 gap-3">
        {/* Revenue Chart */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-border p-4 sm:p-5" data-testid="revenue-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold">Chiffre d&apos;affaires</p>
              <p className="font-mono text-2xl sm:text-3xl font-bold mt-1">{revenue?.month?.toFixed(0) ?? 0}&euro; <span className="text-xs font-normal text-muted-foreground">ce mois</span></p>
            </div>
            <div className="text-right space-y-0.5">
              <p className="text-[10px] text-muted-foreground">Aujourd&apos;hui <span className="font-mono font-bold text-foreground">{revenue?.today?.toFixed(0) ?? 0}&euro;</span></p>
              <p className="text-[10px] text-muted-foreground">Semaine <span className="font-mono font-bold text-foreground">{revenue?.week?.toFixed(0) ?? 0}&euro;</span></p>
            </div>
          </div>
          <div className="h-[100px] sm:h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenue?.daily || []} barSize={window.innerWidth < 640 ? 24 : 32}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f4f4f5" />
                <Bar dataKey="revenue" fill="#2563EB" radius={[6, 6, 0, 0]} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip formatter={(v) => [`${v}\u20ac`, "CA"]} labelFormatter={(l) => l} contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #e4e4e7", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }} cursor={{ fill: "rgba(37,99,235,0.05)" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Insights */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-4 sm:p-5" data-testid="insights-section">
          <p className="text-sm font-semibold mb-3">Insights</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F8F8F8] rounded-lg p-3">
              <Scissors size={14} className="text-[#2563EB] mb-1.5" />
              <p className="font-mono text-sm font-bold truncate">{insights?.popular_service || "-"}</p>
              <p className="text-[9px] text-muted-foreground">Service #1</p>
            </div>
            <div className="bg-[#F8F8F8] rounded-lg p-3">
              <Star size={14} className="text-orange-500 mb-1.5" />
              <p className="font-mono text-sm font-bold">{insights?.avg_ticket ?? 0}&euro;</p>
              <p className="text-[9px] text-muted-foreground">Ticket moyen</p>
            </div>
            <div className="bg-[#F8F8F8] rounded-lg p-3">
              <BarChart3 size={14} className="text-green-600 mb-1.5" />
              <p className="font-mono text-sm font-bold">{insights?.occupancy ?? 0}%</p>
              <p className="text-[9px] text-muted-foreground">Taux occupation</p>
            </div>
            <div className="bg-[#F8F8F8] rounded-lg p-3">
              <UserCheck size={14} className="text-purple-600 mb-1.5" />
              <p className="font-mono text-sm font-bold">{insights?.retention_rate ?? 0}%</p>
              <p className="text-[9px] text-muted-foreground">Fid&eacute;lisation</p>
            </div>
          </div>
          {insights?.service_breakdown?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {insights.service_breakdown.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground truncate mr-2">{s.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-[#2563EB] rounded-full" style={{ width: `${(s.count / (insights.service_breakdown[0]?.count || 1)) * 100}%` }} />
                    </div>
                    <span className="font-mono font-bold w-6 text-right">{s.count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subscription Warning */}
      {stats?.subscription?.plan === "free" && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-orange-800">Plan Gratuit &mdash; {stats.month}/20 r&eacute;servations</p>
            <p className="text-xs text-orange-600 mt-0.5">Passez &agrave; Pro pour des r&eacute;servations illimit&eacute;es. 0% commission.</p>
          </div>
          <Button size="sm" onClick={() => navigate("/tableau-de-bord/parametres")} className="rounded-full bg-orange-600 hover:bg-orange-700 text-xs shrink-0" data-testid="upgrade-prompt-btn">Passer &agrave; Pro</Button>
        </div>
      )}

      {/* Today's Appointments */}
      <div className="bg-white rounded-xl border border-border">
        <div className="p-4 sm:p-5 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-sm sm:text-base">Rendez-vous du jour</h2>
          <Button size="sm" variant="outline" onClick={() => navigate("/tableau-de-bord/calendrier")} className="rounded-full text-xs gap-1 h-8" data-testid="view-calendar-btn">
            Calendrier <ArrowRight size={14} />
          </Button>
        </div>
        {todayAppts.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3"><Calendar size={22} className="text-muted-foreground/40" /></div>
            <p className="text-sm font-medium text-muted-foreground">Aucun rendez-vous aujourd&apos;hui</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">Partagez votre lien de r&eacute;servation ou ajoutez un walk-in !</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {todayAppts.map((appt) => (
              <div key={appt.id} className="p-3 sm:p-4 flex items-center justify-between gap-2 hover:bg-accent/20 transition-colors" data-testid={`appointment-${appt.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col items-center min-w-[42px]">
                    <p className="font-mono text-sm font-bold">{appt.start_time}</p>
                    <p className="font-mono text-[9px] text-muted-foreground">{appt.end_time}</p>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-heading font-bold text-[#2563EB] text-xs shrink-0">
                    {appt.client_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{appt.client_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{appt.service_name} &middot; {appt.price}&euro;</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {appt.status === "confirmed" ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(appt.id, "completed")} className="h-7 px-2 text-[10px] rounded-full gap-1 text-green-700 border-green-200 hover:bg-green-50" data-testid={`complete-${appt.id}`}>
                        <Check size={12} /> Fait
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(appt.id, "cancelled")} className="h-7 w-7 p-0 rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid={`cancel-${appt.id}`}>
                        <XIcon size={12} />
                      </Button>
                    </>
                  ) : (
                    <Badge className={`${statusColors[appt.status]} text-[10px] font-medium border-0`}>{statusLabels[appt.status]}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Walk-in Dialog */}
      <Dialog open={walkinOpen} onOpenChange={setWalkinOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nouveau walk-in</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm mb-1.5 block">Service *</Label>
              <Select value={walkinForm.service_id} onValueChange={v => setWalkinForm({ ...walkinForm, service_id: v })}>
                <SelectTrigger className="h-11" data-testid="walkin-service"><SelectValue placeholder="Choisir un service" /></SelectTrigger>
                <SelectContent>
                  {services.filter(s => s.active).map(s => <SelectItem key={s.id} value={s.id}>{s.name} - {s.price}&euro;</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Nom du client *</Label>
              <Input value={walkinForm.client_name} onChange={e => setWalkinForm({ ...walkinForm, client_name: e.target.value })} placeholder="Nom" className="h-11" data-testid="walkin-name" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">T&eacute;l&eacute;phone</Label>
              <Input value={walkinForm.client_phone} onChange={e => setWalkinForm({ ...walkinForm, client_phone: e.target.value })} placeholder="Optionnel" className="h-11" data-testid="walkin-phone" />
            </div>
            <Button onClick={handleWalkin} disabled={walkinLoading} className="w-full rounded-full h-11 bg-[#2563EB] hover:bg-blue-700" data-testid="walkin-submit">
              {walkinLoading ? "Ajout..." : "Ajouter le walk-in"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
